import { FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Activity, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

export function Login() {
  const { login, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already logged in (e.g. came here manually) → bounce to /chart
  if (!loading && isAuthenticated) {
    const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || "/chart";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
      // Navigate via the redirect above on the next render
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || "Login failed. Check your credentials and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex h-screen items-center justify-center overflow-hidden bg-bg-deep">
      {/* Background grid — subtle, breaks the flat dark */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(#2a2e39 1px, transparent 1px), linear-gradient(90deg, #2a2e39 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 60% 50% at 50% 50%, #000 30%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 60% 50% at 50% 50%, #000 30%, transparent 80%)",
        }}
      />

      {/* Brand glow */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -z-0 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/10 blur-3xl"
      />

      <div className="relative z-10 w-full max-w-sm animate-fade-in px-6">
        {/* Brand */}
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <Activity className="h-7 w-7 text-accent" strokeWidth={2.5} />
          <span className="text-xl font-bold tracking-tight text-text-primary">
            Democrat<span className="text-accent">Charts</span>
          </span>
        </div>

        <div className="rounded-lg border border-border bg-bg-panel p-8 shadow-elevated">
          <div className="mb-6">
            <h1 className="text-lg font-semibold text-text-primary">Sign in</h1>
            <p className="mt-1 text-xs text-text-secondary">
              Replay, backtest, and trade — for the family.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
            <Input
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-bear/30 bg-bear/10 p-2.5 text-xs text-bear">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" size="lg" loading={submitting} className="mt-2">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-2xs text-text-muted">
          Accounts are created by your admin. Contact them for access.
        </p>
      </div>
    </div>
  );
}
